import React from 'react';
import { Link } from 'react-router-dom';
import { Zap, ArrowLeft, Target, Users, Lightbulb, Shield, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';

const AboutUs = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-600 to-indigo-700 flex items-center justify-center">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="font-heading font-bold text-xl text-slate-900">Nexus Leads</span>
          </Link>
          <Link to="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-indigo-600 to-purple-700 py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-white mb-4">
            About Nexus Leads
          </h1>
          <p className="text-xl text-indigo-100 max-w-2xl mx-auto">
            Empowering businesses with AI-driven lead generation to accelerate growth and success.
          </p>
        </div>
      </section>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Our Story */}
        <div className="bg-white rounded-2xl border border-slate-200 p-8 mb-8">
          <h2 className="text-2xl font-heading font-bold text-slate-900 mb-4">Our Story</h2>
          <p className="text-slate-600 leading-relaxed mb-4">
            Nexus Leads was born from a simple observation: businesses spend countless hours searching 
            for quality leads, often with inconsistent results. We believed there had to be a better way.
          </p>
          <p className="text-slate-600 leading-relaxed mb-4">
            Built by <a href="https://www.nexusresolution.biz" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline font-medium">Nexus Resolution</a>, 
            a company dedicated to providing innovative business solutions, Nexus Leads combines the power 
            of artificial intelligence with comprehensive business data to deliver high-quality leads 
            tailored to your specific needs.
          </p>
          <p className="text-slate-600 leading-relaxed">
            Our platform is designed specifically for the US market, helping businesses of all sizes 
            find and connect with potential customers in any niche, across all 50 states.
          </p>
        </div>

        {/* Our Mission */}
        <div className="bg-white rounded-2xl border border-slate-200 p-8 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
              <Target className="w-6 h-6 text-indigo-600" />
            </div>
            <h2 className="text-2xl font-heading font-bold text-slate-900">Our Mission</h2>
          </div>
          <p className="text-slate-600 leading-relaxed">
            To democratize access to high-quality business leads by leveraging AI technology, 
            enabling businesses of all sizes to compete effectively in today's market. We believe 
            every business deserves the tools to grow, regardless of their budget or technical expertise.
          </p>
        </div>

        {/* What Sets Us Apart */}
        <div className="bg-white rounded-2xl border border-slate-200 p-8 mb-8">
          <h2 className="text-2xl font-heading font-bold text-slate-900 mb-6">What Sets Us Apart</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex gap-4">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <Lightbulb className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">AI-Powered Intelligence</h3>
                <p className="text-slate-600 text-sm">
                  Our AI analyzes businesses to provide actionable insights, personalized email 
                  templates, and effective call scripts.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Target className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">Precision Targeting</h3>
                <p className="text-slate-600 text-sm">
                  Filter leads by niche, location, ratings, and reviews to find exactly 
                  the businesses you want to reach.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">Built for Sales Teams</h3>
                <p className="text-slate-600 text-sm">
                  From solo entrepreneurs to sales agencies, our platform scales to meet 
                  your team's needs.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">Real Business Data</h3>
                <p className="text-slate-600 text-sm">
                  Access verified business information including websites, phone numbers, 
                  addresses, and more.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Parent Company */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-8 text-white">
          <h2 className="text-2xl font-heading font-bold mb-4">Powered by Nexus Resolution</h2>
          <p className="text-slate-300 leading-relaxed mb-6">
            Nexus Leads is a product of <strong className="text-white">Nexus Resolution</strong>, 
            a forward-thinking company committed to delivering innovative business solutions. 
            With a focus on leveraging technology to solve real business challenges, Nexus Resolution 
            continues to develop tools that help businesses thrive in the digital age.
          </p>
          <a 
            href="https://www.nexusresolution.biz" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white text-slate-900 px-6 py-3 rounded-lg font-medium hover:bg-slate-100 transition-colors"
          >
            Visit Nexus Resolution
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>

        {/* Contact CTA */}
        <div className="mt-8 text-center">
          <p className="text-slate-600 mb-4">
            Have questions or want to learn more about Nexus Leads?
          </p>
          <a 
            href="mailto:support@nexusleads.us" 
            className="text-indigo-600 hover:text-indigo-700 font-medium"
          >
            Contact us at support@nexusleads.us
          </a>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 bg-slate-900 mt-12">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-slate-400 text-sm">
            © 2026 Nexus Leads. A product of{' '}
            <a 
              href="https://www.nexusresolution.biz" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-slate-300 hover:text-white"
            >
              Nexus Resolution
            </a>
            . All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default AboutUs;
